import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, Check, AlertCircle } from "lucide-react";
import { field, darkBtn } from "@/lib/adminApi";

const MAX = 50 * 1024 * 1024;

export default function MediaUploader({ api, themes, onDone }) {
  const [files, setFiles] = useState([]);
  const [theme, setTheme] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);

  const add = (list) => {
    const next = Array.from(list)
      .filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"))
      .map((f) => ({ file: f, state: f.size > MAX ? "error" : "ready", error: f.size > MAX ? "Plus de 50 Mo" : null }));
    setFiles((cur) => [...cur, ...next]);
  };

  const upload = async () => {
    setBusy(true);
    let ok = 0;
    let failed = 0;
    const out = [...files];
    for (let i = 0; i < out.length; i++) {
      if (out[i].state !== "ready") continue;
      out[i] = { ...out[i], state: "uploading" };
      setFiles([...out]);
      const fd = new FormData();
      fd.append("theme", theme);
      fd.append("caption", caption);
      fd.append("file", out[i].file);
      try {
        await api.post("/admin/media", fd);
        out[i] = { ...out[i], state: "done" };
        ok++;
      } catch (e) {
        out[i] = { ...out[i], state: "error", error: e.response?.data?.detail || `Erreur ${e.response?.status || "réseau"}` };
        failed++;
      }
      setFiles([...out]);
    }
    setBusy(false);
    if (ok) toast.success(`${ok} média(s) publié(s) dans « ${theme} ».`);
    if (failed) toast.error(`${failed} fichier(s) n'ont pas pu être envoyés.`);
    setFiles(out.filter((f) => f.state !== "done"));
    setCaption("");
    if (input.current) input.current.value = "";
    onDone();
  };

  const ready = files.filter((f) => f.state === "ready").length;

  return (
    <section data-testid="media-uploader" className="rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
      <h2 className="font-display text-lg font-semibold text-stone-900">Ajouter des médias</h2>
      <div
        data-testid="media-dropzone"
        onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors duration-300 ${drag ? "border-[#C5A059] bg-[#C5A059]/10" : "border-stone-300 hover:border-[#C5A059]"}`}
      >
        <UploadCloud className="h-7 w-7 text-[#C5A059]" />
        <p className="mt-2 text-sm font-medium text-stone-700">Glissez vos photos et vidéos ici</p>
        <p className="text-xs text-stone-400">ou cliquez pour choisir — plusieurs fichiers possibles, 50 Mo max chacun</p>
        <input ref={input} data-testid="media-file-input" type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => add(e.target.files)} />
      </div>

      {files.length > 0 && (
        <ul data-testid="media-upload-queue" className="mt-3 max-h-48 space-y-1.5 overflow-y-auto">
          {files.map((f, i) => (
            <li key={`${f.file.name}-${i}`} data-testid={`media-queue-item-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#FAF8F5] px-3 py-2 text-xs">
              <span className="truncate text-stone-700">{f.file.name}</span>
              <span className="flex shrink-0 items-center gap-2">
                {f.state === "uploading" && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#C5A059]" />}
                {f.state === "done" && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                {f.state === "error" && <span className="flex items-center gap-1 text-[#C85A32]"><AlertCircle className="h-3.5 w-3.5" />{f.error}</span>}
                {!busy && (
                  <button data-testid={`media-queue-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Retirer">
                    <X className="h-3.5 w-3.5 text-stone-400 hover:text-stone-700" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input data-testid="media-theme-input" list="media-theme-list" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Thématique (choisir ou créer)" maxLength={60} className={field} />
        <datalist id="media-theme-list">{themes.map((t) => <option key={t} value={t} />)}</datalist>
        <input data-testid="media-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Légende commune (facultatif)" maxLength={140} className={field} />
      </div>
      <button data-testid="media-upload-button" onClick={upload} disabled={busy || !ready || !theme.trim()} className={`${darkBtn} mt-4 w-full py-3`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
        {busy ? "Envoi en cours…" : `Publier ${ready || ""} média${ready > 1 ? "s" : ""}`}
      </button>
    </section>
  );
}
